import { useState } from 'react';
import { useLocalStorage } from './useLocalStorage';
import {
  Workstream,
  Synergy,
  IntegrationMilestone
} from '../types/integration';
import {
  sampleWorkstreams,
  sampleSynergies,
  sampleMilestones
} from '../data/sampleData';

export function useIntegration(dealId?: string) {
  const [workstreams, setWorkstreams] = useLocalStorage<Workstream[]>('integration_workstreams', sampleWorkstreams);
  const [synergies, setSynergies] = useLocalStorage<Synergy[]>('integration_synergies', sampleSynergies);
  const [milestones, setMilestones] = useLocalStorage<IntegrationMilestone[]>('integration_milestones', sampleMilestones);
  const [loading, setLoading] = useState(false);

  const filteredWorkstreams = dealId ? workstreams.filter(w => w.dealId === dealId) : workstreams;
  const filteredSynergies = dealId ? synergies.filter(s => s.dealId === dealId) : synergies;
  const filteredMilestones = dealId ? milestones.filter(m => m.dealId === dealId) : milestones;

  const addWorkstream = async (workstream: Omit<Workstream, 'id'>) => {
    setLoading(true);
    try {
      const newWorkstream: Workstream = {
        ...workstream,
        id: crypto.randomUUID()
      };
      setWorkstreams([...workstreams, newWorkstream]);
      return newWorkstream;
    } finally {
      setLoading(false);
    }
  };

  const updateWorkstream = async (workstreamId: string, updates: Partial<Workstream>) => {
    setLoading(true);
    try {
      setWorkstreams(workstreams.map(workstream =>
        workstream.id === workstreamId
          ? { ...workstream, ...updates }
          : workstream
      ));
    } finally {
      setLoading(false);
    }
  };

  const addSynergy = async (synergy: Omit<Synergy, 'id'>) => {
    setLoading(true);
    try {
      const newSynergy: Synergy = {
        ...synergy,
        id: crypto.randomUUID()
      };
      setSynergies([...synergies, newSynergy]);
      return newSynergy;
    } finally {
      setLoading(false);
    }
  };

  const updateSynergy = async (synergyId: string, updates: Partial<Synergy>) => {
    setLoading(true);
    try {
      setSynergies(synergies.map(synergy =>
        synergy.id === synergyId
          ? { ...synergy, ...updates }
          : synergy
      ));
    } finally {
      setLoading(false);
    }
  };

  const addMilestone = async (milestone: Omit<IntegrationMilestone, 'id'>) => {
    setLoading(true);
    try {
      const newMilestone: IntegrationMilestone = {
        ...milestone,
        id: crypto.randomUUID()
      };
      setMilestones([...milestones, newMilestone]);
      return newMilestone;
    } finally {
      setLoading(false);
    }
  };

  const updateMilestone = async (milestoneId: string, updates: Partial<IntegrationMilestone>) => {
    setLoading(true);
    try {
      setMilestones(milestones.map(milestone =>
        milestone.id === milestoneId
          ? { ...milestone, ...updates }
          : milestone
      ));
    } finally {
      setLoading(false);
    }
  };

  return {
    workstreams: filteredWorkstreams,
    synergies: filteredSynergies,
    milestones: filteredMilestones,
    loading,
    addWorkstream,
    updateWorkstream,
    addSynergy,
    updateSynergy,
    addMilestone,
    updateMilestone
  };
}